import { PIN_HEIGHT, PIN_WIDTH, PinInput, escapeHtml, cssUrl, requireImageUrl } from "./types";

// venueCard — editorial "blog pin" layout: a translucent white card floats
// centered over the photo with a mixed script + bold-serif headline in dark
// ink. Modeled on the high-saving Pinterest wedding aesthetic
// ("Perfect WEDDING Table Settings for a dream day"). Three text tiers map to
// our copy fields:
//   eyebrow  → script lead-in   (Great Vibes)
//   name     → SERIF headline   (uppercase, the dominant line)
//   tagline  → script close-out (Great Vibes)
// Best for detail/tablescape photos where a busy, pretty image wants an
// elegant frame rather than text floating loose. Dark text on the light card
// (unlike overlay/centered, which are white text on the photo).
export function venueCardTemplate(input: PinInput): string {
  const { eyebrow, brand, typography, venue } = input;
  const imageUrl = requireImageUrl(input, "venueCard");
  if (!venue) {
    throw new Error("venueCard template requires venue { name, location?, tagline? }");
  }

  // Headline (serif, uppercase) size ladder — fits ~2 lines inside the card.
  const nameLen = venue.name.length;
  const nameSize = nameLen <= 16 ? 70 : nameLen <= 26 ? 60 : nameLen <= 38 ? 50 : 44;

  return `<!doctype html>
<html><head>
<meta charset="utf-8" />
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link rel="stylesheet" href="${typography.googleFontsHref}" />
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap" />
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  html,body{width:${PIN_WIDTH}px;height:${PIN_HEIGHT}px;font-family:${typography.bodyFont};-webkit-font-smoothing:antialiased;background:#000}
  .pin{position:relative;width:100%;height:100%;overflow:hidden}
  .image{position:absolute;inset:0;background-image:url('${cssUrl(imageUrl)}');background-size:cover;background-position:center}
  /* Light overall scrim so the card's edges read cleanly on any photo. */
  .image::after{content:"";position:absolute;inset:0;background:rgba(0,0,0,.12)}
  /* Translucent card, vertically centered, generous side insets. */
  .card{position:absolute;top:50%;left:84px;right:84px;transform:translateY(-50%);z-index:2;
    background:rgba(255,255,255,.88);backdrop-filter:blur(3px);-webkit-backdrop-filter:blur(3px);
    padding:58px 48px;text-align:center;color:#241f1b;border-radius:4px;
    box-shadow:0 24px 64px rgba(0,0,0,.22)}
  /* Script lead-in (eyebrow). */
  .lead{font-family:'Great Vibes',cursive;font-size:92px;line-height:.86;color:#2a2320}
  /* Serif headline — the dominant, uppercase line. */
  .headline{font-family:${typography.headlineFont};font-weight:${typography.headlineWeight};
    text-transform:uppercase;font-size:${nameSize}px;line-height:1.05;letter-spacing:.5px;
    margin:10px 0 6px;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}
  /* Script close-out (tagline). */
  .tagline{font-family:'Great Vibes',cursive;font-size:62px;line-height:.95;color:#2a2320}
  /* Small brand kicker keeps the client's name on the pin without breaking
     the editorial look. */
  .kicker{font-family:${typography.bodyFont};text-transform:uppercase;letter-spacing:3.5px;
    font-size:19px;font-weight:600;color:#7a6c60;margin-top:26px}
</style></head>
<body><div class="pin">
  <div class="image"></div>
  <div class="card">
    ${eyebrow ? `<div class="lead">${escapeHtml(eyebrow)}</div>` : ""}
    <div class="headline">${escapeHtml(venue.name)}</div>
    ${venue.tagline ? `<div class="tagline">${escapeHtml(venue.tagline)}</div>` : ""}
    <div class="kicker">${escapeHtml(brand)}</div>
  </div>
</div></body></html>`;
}
